import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cmsFetch } from "@jambushrushti/services/adminAuth";
import { fetchAllPages } from "@jambushrushti/lib/fetchAllPages";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import { useToast } from "@jambushrushti/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Calendar,
} from "lucide-react";
import { eventDateInput, eventDateLabel } from "@jambushrushti/lib/eventDate";
import {
  IMAGE_ACCEPT,
  toMultipart,
  validateImageFile,
} from "@jambushrushti/lib/multipart";

interface Event {
  id: string;
  title: string;
  description: string | null;
  event_date: string | null;
  location: string | null;
  image_url: string | null;
  is_published: boolean | null;
  created_at: string;
}

const API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const EVENTS_ENDPOINT = `${API_BASE}/jambushrusti/cms/events/`;

async function eventsRequest(url: string, options?: RequestInit) {
  const response = await cmsFetch(url, options);
  if (response.status === 204) {
    return null;
  }
  const payload = await response.json();
  if (!response.ok) {
    const message = payload.detail || payload.message || payload.error;
    const fields = Object.entries(payload)
      .map(
        ([field, errors]) =>
          `${field}: ${Array.isArray(errors) ? errors.join(", ") : String(errors)}`
      )
      .join("; ");
    throw new Error(
      typeof message === "string" ? message : fields || "Events request failed."
    );
  }
  return payload;
}

export default function EventsManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_date: "",
    location: "",
    is_published: true,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: events = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-events"],
    queryFn: async () => {
      const events: Event[] = await fetchAllPages(
        page => eventsRequest(`${EVENTS_ENDPOINT}?page=${page}`),
        payload => {
          const data = payload.data ?? payload;
          const rows = Array.isArray(data) ? data : data.results;
          if (!Array.isArray(rows)) {
            throw new Error("Unexpected events response.");
          }
          return rows.map(row => ({
            ...row,
            id: String(row.id),
            image_url: row.image || row.image_url || null,
          }));
        }
      );
      return events.sort((a, b) =>
        eventDateInput(b.event_date).localeCompare(eventDateInput(a.event_date))
      );
    },
  });

  const toPayload = (data: typeof formData) => ({
    ...data,
    title: data.title.trim(),
    event_date: data.event_date || null,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return eventsRequest(EVENTS_ENDPOINT, {
        method: "POST",
        body: toMultipart(toPayload(data), "image", imageFile),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events-public"] });
      toast({ title: "Event added successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding event",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      return eventsRequest(`${EVENTS_ENDPOINT}${encodeURIComponent(id)}/`, {
        method: "PATCH",
        body: toMultipart(toPayload(data), "image", imageFile),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events-public"] });
      toast({ title: "Event updated successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating event",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return eventsRequest(`${EVENTS_ENDPOINT}${encodeURIComponent(id)}/`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events-public"] });
      toast({ title: "Event deleted successfully!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting event",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      event_date: "",
      location: "",
      is_published: true,
    });
    setEditingEvent(null);
    setImageFile(null);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      event_date: eventDateInput(event.event_date),
      location: event.location || "",
      is_published: event.is_published ?? true,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (!formData.event_date) {
      toast({ title: "Date is required", variant: "destructive" });
      return;
    }
    if (!formData.location.trim()) {
      toast({ title: "Location is required", variant: "destructive" });
      return;
    }
    if (!formData.description.trim()) {
      toast({ title: "Description is required", variant: "destructive" });
      return;
    }
    if (!editingEvent && !imageFile) {
      toast({ title: "Event Image is required", variant: "destructive" });
      return;
    }
    if (editingEvent) {
      updateMutation.mutate({ id: editingEvent.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              Events Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Manage community events and gatherings
            </p>
          </div>
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-fit gap-2 md:w-auto">
                <Plus className="h-4 w-4" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingEvent ? "Edit Event" : "Add New Event"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Title *</Label>
                    <Input
                      required
                      maxLength={255}
                      value={formData.title}
                      onChange={e =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="Event title"
                    />
                  </div>
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      required
                      value={formData.event_date}
                      onChange={e =>
                        setFormData({ ...formData, event_date: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Location *</Label>
                    <Input
                      required
                      maxLength={255}
                      value={formData.location}
                      onChange={e =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      placeholder="Event location"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Event Image *</Label>
                    <Input
                      type="file"
                      required={!editingEvent}
                      accept={IMAGE_ACCEPT}
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        if (!file) {
                          return setImageFile(null);
                        }
                        try {
                          validateImageFile(file);
                          setImageFile(file);
                        } catch (error) {
                          e.target.value = "";
                          toast({
                            title: (error as Error).message,
                            variant: "destructive",
                          });
                        }
                      }}
                    />
                    {editingEvent?.image_url && !imageFile && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Keep the current image, or choose a replacement.
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label>Description *</Label>
                    <Textarea
                      required
                      value={formData.description}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Event description..."
                      rows={4}
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Switch
                      checked={formData.is_published}
                      onCheckedChange={checked =>
                        setFormData({ ...formData, is_published: checked })
                      }
                    />
                    <Label>Published (visible on website)</Label>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                  >
                    {(createMutation.isPending || updateMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingEvent ? "Update Event" : "Add Event"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <AdminViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="space-y-3 py-12 text-center" role="alert">
              <p className="text-destructive">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div
            className={
              viewMode === "cards"
                ? "grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                : "grid gap-4"
            }
          >
            {filteredEvents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    No events found. Add your first event!
                  </p>
                </CardContent>
              </Card>
            ) : viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border bg-card md:col-span-2 xl:col-span-3">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Event</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredEvents.map(event => (
                      <tr key={event.id} className="hover:bg-muted/30">
                        <td className="max-w-sm px-4 py-3 font-medium">
                          <span className="line-clamp-1">{event.title}</span>
                        </td>
                        <td className="px-4 py-3">
                          {event.event_date
                            ? eventDateLabel(event.event_date)
                            : "—"}
                        </td>
                        <td className="px-4 py-3">{event.location || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              event.is_published ? "default" : "secondary"
                            }
                          >
                            {event.is_published ? "Published" : "Draft"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(event)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() =>
                                confirm(
                                  "Are you sure you want to delete this event?"
                                ) && deleteMutation.mutate(event.id)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              filteredEvents.map(event => (
                <Card key={event.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {event.image_url && (
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="h-24 w-24 shrink-0 rounded-lg object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h3 className="line-clamp-1 font-semibold text-foreground">
                              {event.title}
                            </h3>
                            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                              {event.event_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {eventDateLabel(event.event_date)}
                                </span>
                              )}
                              {event.location && (
                                <span>• {event.location}</span>
                              )}
                            </div>
                          </div>
                          <Badge
                            className="w-fit"
                            variant={
                              event.is_published ? "default" : "secondary"
                            }
                          >
                            {event.is_published ? (
                              <Eye className="mr-1 h-3 w-3" />
                            ) : (
                              <EyeOff className="mr-1 h-3 w-3" />
                            )}
                            {event.is_published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {event.description}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(event)}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive hover:text-white"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this event?"
                                )
                              ) {
                                deleteMutation.mutate(event.id);
                              }
                            }}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
